"use client";

import {
  jumpToHeading,
  useActiveHeading,
  useArticleOutline,
} from "@/components/content/article-outline";
import type { HeadingEntry } from "@/components/content/headings";
import { cn } from "@/lib/utils";

const MIN_HEADINGS = 2;

function OutlineList({
  headings,
  activeId,
}: {
  headings: HeadingEntry[];
  activeId: string | null;
}) {
  return (
    <ul className="m-0 list-none p-0">
      {headings.map((heading) => {
        const isActive = activeId === heading.id;
        return (
          <li key={heading.id} className="m-0">
            <a
              href={`#${heading.id}`}
              onClick={(event) => {
                event.preventDefault();
                jumpToHeading(heading.id);
              }}
              aria-current={isActive ? "location" : undefined}
              className={cn(
                "block border-l-2 py-1 pl-3 pr-2 text-sm leading-snug transition-colors hover:text-foreground",
                isActive
                  ? "border-action font-semibold text-foreground"
                  : "border-border text-muted-foreground",
              )}
            >
              {heading.text}
            </a>
          </li>
        );
      })}
    </ul>
  );
}

export function OnThisPage({ articleId }: { articleId: string }) {
  const outline = useArticleOutline(articleId);
  const headings = outline?.headings ?? [];
  const activeId = useActiveHeading(headings);
  if (!outline || headings.length < MIN_HEADINGS) return null;
  return (
    <nav aria-label="On this page">
      <p className="mb-2 text-sm font-semibold text-foreground">On this page</p>
      <OutlineList headings={headings} activeId={activeId} />
    </nav>
  );
}

export function MobileOnThisPage({ articleId }: { articleId: string }) {
  const outline = useArticleOutline(articleId);
  if (!outline || outline.headings.length < MIN_HEADINGS) return null;
  return (
    <details className="rounded-sm border border-border bg-muted px-4 py-3 lg:hidden">
      <summary className="cursor-pointer text-sm font-semibold text-foreground">
        On this page
      </summary>
      <div className="mt-3">
        <OutlineList headings={outline.headings} activeId={null} />
      </div>
    </details>
  );
}
